/**
 * Feb 2026 · Inventory Ledger — read-only audit module.
 *
 * The ledger is now purely historical:
 *   • Materials tab removed (edit codes in Raw Material Master).
 *   • "Lot Traceability" renamed → "Batch History".
 *   • "Transactions" renamed → "Inventory Log".
 *   • Every "+ New" / Add / Edit / Delete affordance is stripped.
 *   • Rows can only be Viewed or Printed. All authoring happens in
 *     GRN, Material Issue, or Production Entry.
 *   • Inventory Log rows now surface Batch No, Source Document
 *     (GRN / MRQ / WO / …) as a clickable link, plus Godown.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { dedupById } from "@/lib/utils";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";
import TwoLineDocNo from "@/components/TwoLineDocNo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PackageIcon, WarningIcon, StackIcon, CoinIcon, DownloadSimpleIcon, EyeIcon, PrinterIcon, LinkIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";
import { exportToCSV } from "@/lib/csv";

const KPI = ({ label, value, unit, icon: Icon, color = "text-white" }) => (
  <div className="tactical-card p-4">
    <div className="flex items-start justify-between mb-3">
      <div className="overline">{label}</div>
      {Icon && (
        <div className="w-7 h-7 rounded-sm bg-[var(--bg-2)] flex items-center justify-center">
          <Icon size={14} className={color} weight="bold" />
        </div>
      )}
    </div>
    <div className={`font-mono text-2xl font-semibold ${color}`}>
      {value}{unit && <span className="text-sm text-zinc-500 ml-1">{unit}</span>}
    </div>
  </div>
);

export default function Inventory() {
  const [tab, setTab] = useState("lots");
  const [materials, setMaterials] = useState([]);
  const [lots, setLots] = useState([]);
  const [txns, setTxns] = useState([]);
  const [machines, setMachines] = useState([]);
  const [orders, setOrders] = useState([]);
  const [godowns, setGodowns] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [viewing, setViewing] = useState(null); // { kind: "lot"|"txn", row }
  // Feb 25, 2026 · Inventory Log batch-number search — matches on
  // batch_no (both txn-level and paired-lot fallback), shade_no from
  // the linked lot, and material code/name so a warehouse operator can
  // jump to any shade lot in one keystroke.
  const [logQuery, setLogQuery] = useState("");

  const load = async () => {
    try {
      const [m, l, t, mach, o, g, k] = await Promise.all([
        api.get("/raw-materials/"),
        api.get("/inventory-lots/"),
        api.get("/inventory-txns/"),
        api.get("/machines/"),
        api.get("/sales-orders/"),
        api.get("/godowns/").catch(() => ({ data: [] })),
        api.get("/dashboard/inventory-kpis"),
      ]);
      setMaterials(m.data); setLots(l.data); setTxns(t.data);
      setMachines(mach.data); setOrders(dedupById(o.data));
      setGodowns(g.data); setKpis(k.data);
    } catch (e) {
      toast.error("Failed to load inventory ledger");
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const rmName = (id) => materials.find(m => m.id === id)?.name || "—";
  const rmCode = (id) => materials.find(m => m.id === id)?.code || "—";
  const rmUom = (id) => materials.find(m => m.id === id)?.uom || "";
  const lotNo = (id) => lots.find(l => l.id === id)?.lot_no || "—";
  const lotBatch = (id) => lots.find(l => l.id === id)?.batch_no || "";
  const lotGrn = (id) => lots.find(l => l.id === id)?.grn_no || "";
  const machineCode = (id) => machines.find(m => m.id === id)?.code || "—";
  const orderNo = (id) => orders.find(o => o.id === id)?.order_no || "—";
  const godownName = (id) => godowns.find(gd => gd.id === id)?.name || "—";

  // Feb 25, 2026 · Every Inventory Log entry now surfaces its source
  // document: GRN → Purchase Receipt list, MRQ / Issue → Material Issue
  // list, WO → Production Orders, TFR → Bin Transfer, and so on. Click
  // navigates the user straight to the originating audit trail.
  const sourceDoc = (t) => {
    const lot = lots.find(l => l.id === t.lot_id);
    const ord = orderNo(t.order_id);
    switch (t.txn_type) {
      case "Receipt":
        return { kind: "GRN", id: lot?.grn_no || t.reference || "—", to: "/inventory/purchase-receipt" };
      case "Opening":
        // Mar 3, 2026 · Opening rows now stamp a proper OPB-FY-NNNNN
        // number on `reference` (and on the paired lot's `lot_no` / `grn_no`).
        return { kind: "OPB", id: t.opb_no || lot?.lot_no || t.reference || "—", to: "/inventory/opening-stock" };
      case "Issue":
      case "Consumption":
      case "Prod-Issue":
        // Feb 2026 · Adopt MI Reference — Issue rows now headline the
        // `MI-<FY>-#####` number when the txn was stamped by an MI event.
        // Legacy Issue rows (pre-split) fall back to the MRQ reference so
        // no historical row loses its audit hook.
        return {
          kind: t.mi_no ? "MI" : "MRQ",
          id: t.mi_no || t.reference || t.mrq_no || "—",
          sub: t.mi_no && t.mrq_no && t.mrq_no !== t.mi_no ? `MIR: ${t.mrq_no}` : "",
          to: "/material-issue/mi",
        };
      case "Prod-Return":
      case "Return":
        return { kind: "RTN", id: t.reference || "—", to: "/material-issue" };
      case "FG-Receipt":
        return { kind: "WO", id: ord !== "—" ? ord : (t.cwo_no || t.reference || "—"), to: "/production-orders" };
      case "Sales-Dispatch":
        return { kind: "SO", id: ord !== "—" ? ord : (t.reference || "—"), to: "/sales-orders" };
      case "Transfer-In":
      case "Transfer-Out":
      case "Bin-Transfer":
        return { kind: "TFR", id: t.transfer_id || t.reference || "—", to: "/inventory/bin-transfer" };
      case "Scrap":
      case "Waste":
        return { kind: "SCRAP", id: t.reference || "—", to: "/inventory/scrap" };
      case "Adjust":
      case "Supplier-Return":
      default: {
        // Mar 3, 2026 · GRN reversals surface as Adjust rows — prefer the
        // paired lot's GRN number (or the explicit grn_no stamp from the
        // reversal doc) over the supplier invoice reference so operators
        // see `ADJUST: GRN-26-27-00007` instead of the raw invoice code.
        const grn = t.grn_no || lot?.grn_no || "";
        if (grn) {
          return { kind: "ADJUST", id: grn, to: "/inventory/purchase-receipt" };
        }
        return { kind: t.txn_type?.toUpperCase() || "REF", id: t.reference || "—", to: null };
      }
    }
  };

  // ---------------- CSV export -------------------------------------
  const exportCSV = () => {
    if (tab === "lots") {
      exportToCSV("inventory-batch-history.csv",
        lots.map(l => ({
          ...l,
          source_doc_no: l.grn_no || l.lot_no,
          source_doc_date: l.received_date,
          material: rmCode(l.raw_material_id),
        })),
        [
          { key: "source_doc_no", label: "Source Document Number" },
          { key: "source_doc_date", label: "Source Document Date" },
          { key: "material", label: "Material" },
          { key: "batch_no", label: "Batch No" },
          { key: "qty_received", label: "Received Qty" },
          { key: "qty_used", label: "Used" },
          { key: "location", label: "Location" }, { key: "status", label: "Status" },
        ]
      );
    } else {
      exportToCSV("inventory-log.csv",
        txns.map(t => {
          const s = sourceDoc(t);
          return {
            ...t,
            material: rmCode(t.raw_material_id),
            batch_no: t.batch_no || lotBatch(t.lot_id),
            godown: godownName(t.godown_id),
            source_doc_no: s.kind && s.id !== "—" ? `${s.kind}: ${s.id}` : "—",
            source_doc_date: t.txn_date,
          };
        }),
        [
          { key: "txn_type", label: "Type" },
          { key: "source_doc_no", label: "Source Document Number" },
          { key: "source_doc_date", label: "Source Document Date" },
          { key: "material", label: "Material" },
          { key: "batch_no", label: "Batch No" },
          { key: "quantity", label: "Received Qty" },
          { key: "godown", label: "Godown" }, { key: "remarks", label: "Remarks" },
        ]
      );
    }
  };

  // ---------------- PDF / print ------------------------------------
  const printPdf = () => {
    const w = window.open("", "_blank", "width=1200,height=900");
    if (!w) { toast.error("Popup blocked — allow popups for this site"); return; }
    const today = new Date().toISOString().slice(0, 10);
    let title = "", headerCols = [], rowsHtml = "";
    if (tab === "lots") {
      title = "Inventory Ledger · Batch History";
      headerCols = ["Source Document Number", "Source Document Date", "Material", "Batch No", "Received Qty", "Used", "Balance", "Location", "Status"];
      rowsHtml = lots.map(l => {
        const bal = (l.qty_received || 0) - (l.qty_used || 0);
        return `
        <tr>
          <td>${l.grn_no || l.lot_no || ""}</td>
          <td>${(l.received_date || "").slice(0, 10)}</td>
          <td>${rmCode(l.raw_material_id)} · ${rmName(l.raw_material_id)}</td>
          <td>${l.batch_no || ""}</td>
          <td class="num">${Number(l.qty_received || 0).toLocaleString("en-IN")}</td>
          <td class="num">${Number(l.qty_used || 0).toLocaleString("en-IN")}</td>
          <td class="num"><b>${Number(bal).toLocaleString("en-IN")}</b></td>
          <td>${l.location || ""}</td>
          <td>${l.status || ""}</td>
        </tr>`;
      }).join("");
    } else {
      title = "Inventory Ledger · Inventory Log";
      headerCols = ["Type", "Source Document Number", "Source Document Date", "Material", "Batch No", "Received Qty", "UoM", "Godown", "Remarks"];
      rowsHtml = txns.map(t => {
        const s = sourceDoc(t);
        return `
        <tr>
          <td>${t.txn_type || ""}</td>
          <td>${s.kind && s.id !== "—" ? `${s.kind}: ${s.id}` : "—"}</td>
          <td>${t.txn_date || ""}</td>
          <td>${rmCode(t.raw_material_id)}</td>
          <td>${t.batch_no || lotBatch(t.lot_id) || ""}</td>
          <td class="num">${(t.txn_type === "Receipt" || t.txn_type === "Return") ? "+" : "−"}${Number(t.quantity || 0).toLocaleString("en-IN")}</td>
          <td>${rmUom(t.raw_material_id)}</td>
          <td>${godownName(t.godown_id)}</td>
          <td>${t.remarks || ""}</td>
        </tr>`;
      }).join("");
    }
    const headerHtml = headerCols.map((h) =>
      `<th${(h === "Qty" || h === "Qty Received" || h === "Used" || h === "Balance") ? ' class="num"' : ""}>${h}</th>`
    ).join("");
    w.document.write(`<!doctype html><html><head><title>${title}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: -apple-system, sans-serif; font-size: 10px; color: #111; }
        h1 { margin: 0 0 4px; font-size: 16px; }
        .meta { color: #666; font-size: 10px; margin-bottom: 8px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { border-bottom: 1px solid #ddd; padding: 4px 6px; text-align: left; vertical-align: top; }
        th { background: #f4f4f4; text-transform: uppercase; font-size: 9px; letter-spacing: .05em; }
        td.num, th.num { text-align: right; font-family: 'Courier New', monospace; }
      </style></head><body>
      <h1>${title}</h1>
      <div class="meta">Generated ${today} · ${rowsHtml ? (rowsHtml.match(/<tr>/g) || []).length : 0} rows</div>
      <table>
        <thead><tr>${headerHtml}</tr></thead>
        <tbody>${rowsHtml || `<tr><td colspan="${headerCols.length}" style="text-align:center;padding:24px;color:#888">No data</td></tr>`}</tbody>
      </table>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300)};</script>
      </body></html>`);
    w.document.close();
  };

  // Print a single row (View dialog + row-level Print icon).
  const printRow = (kind, row) => {
    const w = window.open("", "_blank", "width=900,height=800");
    if (!w) { toast.error("Popup blocked — allow popups for this site"); return; }
    let title = "", pairs = [];
    if (kind === "lot") {
      title = `Batch · ${row.grn_no || row.lot_no}`;
      const bal = (row.qty_received || 0) - (row.qty_used || 0);
      pairs = [
        ["Source Document Number", row.grn_no || row.lot_no],
        ["Source Document Date", row.received_date],
        ["Material", `${rmCode(row.raw_material_id)} · ${rmName(row.raw_material_id)}`],
        ["Batch No", row.batch_no || "—"],
        ["Expiry", row.expiry_date || "—"],
        ["Qty Received", row.qty_received], ["Used", row.qty_used],
        ["Balance", bal], ["Unit Cost", `₹${Number(row.unit_cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
        ["Location", row.location || "—"], ["Status", row.status || "—"],
        ["QC Status", row.quality_status || "—"], ["Shade No", row.shade_no || "—"],
        ["Notes", row.notes || "—"],
      ];
    } else {
      const s = sourceDoc(row);
      title = `Inventory Log · ${row.txn_date}`;
      pairs = [
        ["Type", row.txn_type],
        ["Source Document Number", s.kind && s.id !== "—" ? `${s.kind}: ${s.id}` : "—"],
        ["Source Document Date", row.txn_date],
        ["Material", `${rmCode(row.raw_material_id)} · ${rmName(row.raw_material_id)}`],
        ["Batch No", row.batch_no || lotBatch(row.lot_id) || "—"],
        ["Received Qty", `${(row.txn_type === "Receipt" || row.txn_type === "Return") ? "+" : "−"}${row.quantity} ${rmUom(row.raw_material_id)}`],
        ["Godown", godownName(row.godown_id)],
        ["Machine", machineCode(row.machine_id)], ["Order", orderNo(row.order_id)],
        ["Supplier", row.supplier_name || "—"],
        ["SO/ESO", row.source_order_no || "—"], ["CWO", row.cwo_no || "—"], ["Department", row.department || "—"],
        ["Remarks", row.remarks || "—"],
      ];
    }
    const pairsHtml = pairs.map(([k, v]) => `<tr><th>${k}</th><td>${v ?? "—"}</td></tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>${title}</title>
      <style>
        @page { size: A4; margin: 15mm; }
        body { font-family: -apple-system, sans-serif; font-size: 12px; color: #111; }
        h1 { margin: 0 0 12px; font-size: 18px; }
        table { width: 100%; border-collapse: collapse; }
        th { text-align: left; background: #f4f4f4; padding: 6px 10px; width: 30%; text-transform: uppercase; font-size: 10px; letter-spacing: .05em; }
        td { padding: 6px 10px; border-bottom: 1px solid #eee; }
      </style></head><body>
      <h1>${title}</h1>
      <table>${pairsHtml}</table>
      <script>window.onload=()=>{window.print();setTimeout(()=>window.close(),300)};</script>
      </body></html>`);
    w.document.close();
  };

  const activeMatCount = useMemo(
    () => materials.filter(m => (m.current_stock || 0) > 0 || txns.some(t => t.raw_material_id === m.id)).length,
    [materials, txns]
  );

  const filteredTxns = useMemo(() => {
    const needle = (logQuery || "").trim().toLowerCase();
    if (!needle) return txns;
    return txns.filter(t => {
      const lot = lots.find(l => l.id === t.lot_id);
      const bag = [
        t.batch_no,
        lot?.batch_no,
        lot?.shade_no,
        t.shade_no,
        rmCode(t.raw_material_id),
        rmName(t.raw_material_id),
        t.reference,
      ].filter(Boolean).join(" ").toLowerCase();
      return bag.includes(needle);
    });
  }, [txns, lots, materials, logQuery]);  // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="p-6 space-y-5">
      <div className="flex items-end justify-between">
        <div>
          <div className="overline mb-1">Materials & Traceability</div>
          <h1 className="font-display text-3xl font-black tracking-tight">Inventory Ledger</h1>
          <div className="text-xs text-zinc-500 mt-1">
            Read-only audit trail · same <span className="font-mono text-zinc-300">inventory_ledger</span> source as the
            <Link to="/inventory/stock-book" className="text-[var(--accent)] hover:underline mx-1">Stock Ledger</Link>·
            every row authored in
            <Link to="/inventory/purchase-receipt" className="text-[var(--accent)] hover:underline mx-1">GRN</Link>·
            <Link to="/material-issue" className="text-[var(--accent)] hover:underline mx-1">Material Issue</Link>·
            <Link to="/production/department-entry" className="text-[var(--accent)] hover:underline mx-1">Production Entry</Link>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={exportCSV} data-testid="inv-export-csv" className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <DownloadSimpleIcon size={14} weight="bold" />Export CSV
          </button>
          <button data-testid="inv-print-pdf" onClick={printPdf} className="h-10 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm font-semibold rounded-sm flex items-center gap-2">
            <PrinterIcon size={14} weight="bold" />Print / PDF
          </button>
        </div>
      </div>

      {kpis && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3">
          <KPI label="Materials" value={activeMatCount} icon={PackageIcon} />
          <KPI label="Active Lots" value={kpis.active_lots} icon={StackIcon} color="text-blue-400" />
          <KPI label="Low Stock" value={kpis.low_stock_count} icon={WarningIcon} color="text-red-400" />
          <KPI label="Stock Value" value={`₹${(kpis.stock_value / 1000).toFixed(1)}k`} icon={CoinIcon} color="text-emerald-400" />
          <KPI label="Lot Qty" value={kpis.active_lot_qty} icon={StackIcon} />
        </div>
      )}

      {kpis?.low_stock_count > 0 && (
        <div className="tactical-card p-4 border-l-2 border-l-red-500">
          <div className="flex items-center gap-2 mb-3">
            <WarningIcon size={16} weight="fill" className="text-red-400" />
            <div className="font-display text-sm font-bold">Low Stock Alert · {kpis.low_stock_count} item(s) below reorder point</div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            {kpis.low_stock_items.map(i => (
              <div key={i.code} className="p-2 bg-red-500/5 border border-red-500/20 rounded-sm">
                <div className="font-mono font-semibold">{i.code}</div>
                <div className="text-zinc-400 truncate">{i.name}</div>
                <div className="font-mono mt-1"><span className="text-red-400">{i.current}</span><span className="text-zinc-500"> / min {i.min}</span></div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs — Feb 25, 2026 · Materials tab removed (manage codes in
          Raw Material Master). Two read-only ledger views remain. */}
      <div className="flex gap-2 border-b border-[var(--border)]">
        {[
          { key: "lots", label: `Batch History (${lots.length})` },
          { key: "txns", label: `Inventory Log (${txns.length})` },
        ].map(t => (
          <button
            key={t.key}
            data-testid={`inv-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "lots" && (
        <div className="tactical-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-5 py-3 font-medium">Source Document Number</th>
                <th className="text-left px-5 py-3 font-medium">Source Document Date</th>
                <th className="text-left px-5 py-3 font-medium">Material</th>
                <th className="text-left px-5 py-3 font-medium">Batch No</th>
                <th className="text-right px-5 py-3 font-medium">Received Qty</th>
                <th className="text-right px-5 py-3 font-medium">Used</th>
                <th className="text-right px-5 py-3 font-medium">Balance</th>
                <th className="text-left px-5 py-3 font-medium">Location</th>
                <th className="text-left px-5 py-3 font-medium">Status</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lots.map(l => {
                const bal = (l.qty_received || 0) - (l.qty_used || 0);
                return (
                  <tr key={l.id} className="border-b border-[var(--border)]" data-testid={`lot-${l.lot_no}`}>
                    <td className="px-5 py-3 text-right"><TwoLineDocNo value={l.grn_no || l.lot_no} className="text-white" /></td>
                    <td className="px-5 py-3 font-mono text-zinc-400">{l.received_date}</td>
                    <td className="px-5 py-3">{rmCode(l.raw_material_id)} · {rmName(l.raw_material_id)}</td>
                    <td className="px-5 py-3 font-mono text-zinc-300">{l.batch_no || "—"}</td>
                    <td className="px-5 py-3 text-right font-mono">{l.qty_received}</td>
                    <td className="px-5 py-3 text-right font-mono text-zinc-400">{l.qty_used}</td>
                    <td className="px-5 py-3 text-right font-mono text-emerald-400">{bal}</td>
                    <td className="px-5 py-3 text-zinc-400">{l.location}</td>
                    <td className="px-5 py-3">{(() => {
                      // Mar 3, 2026 · Voided lots (from GRN reversal) win over
                      // every other status so the Batch History matches the
                      // audit trail. Void guard runs BEFORE QC / lifecycle
                      // checks — a reversed lot must never render as
                      // "Awaiting" or "Pass".
                      if (l.void === true || l.quality_status === "Voided" || l.status === "Voided") {
                        return <StatusBadge status="Void" />;
                      }
                      // Mar 1, 2026 · The `l.status` field only tracks the
                      // lot lifecycle (Available / Consumed / Rejected /
                      // Hold). QC gating lives on `quality_status` — an
                      // "Awaiting Inspection" lot MUST NOT render as
                      // "Pass" just because it still has balance.
                      const qc = l.quality_status || "Awaiting Inspection";
                      if (qc === "Rejected") return <StatusBadge status="Reject" />;
                      if (qc === "Hold")     return <StatusBadge status="Hold" />;
                      if (qc !== "Passed" && qc !== "Pass") return <StatusBadge status="Awaiting" />;
                      // QC has cleared the lot → fall through to lifecycle status.
                      if (l.status === "Consumed") return <StatusBadge status="Closed" />;
                      return <StatusBadge status="Pass" />;
                    })()}</td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => setViewing({ kind: "lot", row: l })} title="View batch" data-testid={`lot-view-${l.lot_no}`} className="p-1.5 rounded-sm hover:bg-sky-500/10 text-sky-400"><EyeIcon size={14} /></button>
                      <button onClick={() => printRow("lot", l)} title="Print / PDF" data-testid={`lot-print-${l.lot_no}`} className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><PrinterIcon size={14} /></button>
                    </td>
                  </tr>
                );
              })}
              {lots.length === 0 && <tr><td colSpan={10} className="text-center py-12 text-zinc-500">No batches recorded — book a GRN to see batch history.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "txns" && (
        <div className="tactical-card overflow-x-auto">
          <div className="flex items-center gap-2 px-5 py-3 border-b border-[var(--border)]">
            <div className="relative flex-1 max-w-md">
              <MagnifyingGlassIcon size={13} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
              <input
                data-testid="log-batch-search"
                value={logQuery}
                onChange={e => setLogQuery(e.target.value)}
                placeholder="Search batch / shade / material…"
                className="w-full h-9 pl-9 pr-9 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-sm font-mono focus:outline-none focus:border-[var(--accent)]"
              />
              {logQuery && (
                <button
                  data-testid="log-batch-search-clear"
                  onClick={() => setLogQuery("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-sm hover:bg-[var(--bg-2)] text-zinc-500 hover:text-white"
                  title="Clear search"
                ><XIcon size={12} weight="bold" /></button>
              )}
            </div>
            <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">
              {logQuery ? `${filteredTxns.length} of ${txns.length}` : `${txns.length} entries`}
            </span>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]">
                <th className="text-left px-5 py-3 font-medium">Type</th>
                <th className="text-left px-5 py-3 font-medium">Source Document Number</th>
                <th className="text-left px-5 py-3 font-medium">Source Document Date</th>
                <th className="text-left px-5 py-3 font-medium">Material</th>
                <th className="text-left px-5 py-3 font-medium">Batch No</th>
                <th className="text-right px-5 py-3 font-medium">Received Qty</th>
                <th className="text-left px-5 py-3 font-medium">Godown</th>
                <th className="text-left px-5 py-3 font-medium">Remarks</th>
                <th className="text-right px-5 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTxns.map(t => {
                const s = sourceDoc(t);
                const batch = t.batch_no || lotBatch(t.lot_id);
                return (
                  <tr key={t.id} className="border-b border-[var(--border)]" data-testid={`txn-${t.id}`}>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-0.5 rounded-sm text-xs font-medium ${t.txn_type === "Receipt" ? "bg-emerald-500/10 text-emerald-400" : t.txn_type === "Waste" || t.txn_type === "Scrap" ? "bg-red-500/10 text-red-400" : "bg-amber-500/10 text-amber-400"}`}>
                        {t.txn_type}
                      </span>
                    </td>
                    <td className="px-5 py-3 font-mono text-xs" data-testid={`txn-source-${t.id}`}>
                      {s.to && s.id && s.id !== "—" ? (
                        <Link to={s.to} className="inline-flex flex-col gap-0.5 hover:underline" title={`Open ${s.kind} · ${s.id}`}>
                          <span className={`inline-flex items-center gap-1 ${s.kind === "MI" ? "text-emerald-300 font-bold" : "text-[var(--accent)]"}`}>
                            <LinkIcon size={11} weight="bold" />
                            <span className={s.kind === "MI" ? "text-emerald-400/60" : "text-zinc-500"}>{s.kind}:</span>
                            <span>{s.id}</span>
                          </span>
                          {s.sub && <span className="text-[9px] text-zinc-500 pl-4">{s.sub}</span>}
                          {t.issue_source && (
                            <span data-testid={`txn-src-${t.id}`}
                                  className={`inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 mt-0.5 rounded-sm border w-fit ${t.issue_source === "Automated" ? "bg-sky-500/15 text-sky-300 border-sky-500/40" : "bg-amber-500/15 text-amber-300 border-amber-500/40"}`}>
                              {t.issue_source === "Automated" ? "🤖 Auto" : "👤 Manual"}
                            </span>
                          )}
                        </Link>
                      ) : (
                        <span className="text-zinc-500">{s.kind ? `${s.kind}: ${s.id}` : "—"}</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-zinc-400">{t.txn_date}</td>
                    <td className="px-5 py-3">{rmCode(t.raw_material_id)}</td>
                    <td className="px-5 py-3 font-mono text-zinc-300" data-testid={`txn-batch-${t.id}`}>{batch || "—"}</td>
                    <td className={`px-5 py-3 text-right font-mono ${t.txn_type === "Receipt" ? "text-emerald-400" : "text-red-400"}`}>
                      {t.txn_type === "Receipt" || t.txn_type === "Return" ? "+" : "−"}{t.quantity} {rmUom(t.raw_material_id)}
                    </td>
                    <td className="px-5 py-3 text-zinc-300" data-testid={`txn-godown-${t.id}`}>{godownName(t.godown_id)}</td>
                    <td className="px-5 py-3 text-zinc-400 text-xs">{t.remarks}</td>
                    <td className="px-5 py-3 text-right">
                      {/* Read-only ledger · no Edit / Delete. Reverse the
                          originating GRN / MRQ / WO to void a row. */}
                      <button onClick={() => setViewing({ kind: "txn", row: t })} title="View" data-testid={`txn-view-${t.id}`} className="p-1.5 rounded-sm hover:bg-sky-500/10 text-sky-400"><EyeIcon size={14} /></button>
                      <button onClick={() => printRow("txn", t)} title="Print / PDF" data-testid={`txn-print-${t.id}`} className="p-1.5 rounded-sm hover:bg-emerald-500/10 text-emerald-400"><PrinterIcon size={14} /></button>
                    </td>
                  </tr>
                );
              })}
              {filteredTxns.length === 0 && <tr><td colSpan={9} className="text-center py-12 text-zinc-500">{logQuery ? `No entries matching "${logQuery}".` : "No inventory movements yet — post a GRN, Material Issue, or Production Entry."}</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* View Dialog · read-only for the active row */}
      <Dialog open={!!viewing} onOpenChange={(o) => !o && setViewing(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] max-w-2xl" data-testid="inv-view-dialog" description="Inventory action — opening-balance lot adjustment on the selected raw material.">
          <DialogHeader><DialogTitle className="font-display flex items-center gap-2">
            <EyeIcon size={16} weight="bold" className="text-sky-400" />
            {viewing?.kind === "lot"
              ? `Batch · ${viewing?.row?.grn_no || viewing?.row?.lot_no}`
              : `Inventory Log · ${viewing?.row?.txn_date || ""}`}
          </DialogTitle></DialogHeader>
          {viewing && (() => {
            const r = viewing.row;
            let pairs = [];
            if (viewing.kind === "lot") {
              const bal = (r.qty_received || 0) - (r.qty_used || 0);
              pairs = [
                ["Source Document Number", r.grn_no || r.lot_no],
                ["Source Document Date", r.received_date],
                ["Material", `${rmCode(r.raw_material_id)} · ${rmName(r.raw_material_id)}`],
                ["Batch No", r.batch_no || "—"],
                ["Expiry", r.expiry_date || "—"],
                ["Qty Received", r.qty_received], ["Used", r.qty_used],
                ["Balance", bal], ["Unit Cost", `₹${Number(r.unit_cost || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
                ["Location", r.location || "—"], ["Status", r.status || "—"],
                ["QC Status", r.quality_status || "—"], ["Shade No", r.shade_no || "—"],
                ["Notes", r.notes || "—"],
              ];
            } else {
              const s = sourceDoc(r);
              pairs = [
                ["Type", r.txn_type],
                ["Source Document Number", s.kind && s.id !== "—" ? `${s.kind}: ${s.id}` : "—"],
                ["Source Document Date", r.txn_date],
                ["Material", `${rmCode(r.raw_material_id)} · ${rmName(r.raw_material_id)}`],
                ["Batch No", r.batch_no || lotBatch(r.lot_id) || "—"],
                ["Received Qty", `${(r.txn_type === "Receipt" || r.txn_type === "Return") ? "+" : "−"}${r.quantity} ${rmUom(r.raw_material_id)}`],
                ["Godown", godownName(r.godown_id)],
                ["Machine", machineCode(r.machine_id)], ["Order", orderNo(r.order_id)],
                ["Reference", r.reference || "—"], ["Supplier", r.supplier_name || "—"],
                ["SO/ESO", r.source_order_no || "—"], ["CWO", r.cwo_no || "—"], ["Department", r.department || "—"],
                ["Remarks", r.remarks || "—"],
              ];
            }
            return (
              <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs pt-2">
                {pairs.map(([k, v]) => (
                  <div key={k} className={k === "Remarks" || k === "Notes" ? "col-span-2" : ""}>
                    <div className="overline mb-0.5">{k}</div>
                    <div className="text-white font-mono break-words">{v ?? "—"}</div>
                  </div>
                ))}
              </div>
            );
          })()}
          <DialogFooter>
            <button onClick={() => setViewing(null)} className="h-9 px-4 border border-[var(--border)] hover:bg-[var(--bg-2)] text-sm rounded-sm">Close</button>
            <button data-testid="inv-view-print" onClick={() => { const v = viewing; setViewing(null); printRow(v.kind, v.row); }} className="h-9 px-4 bg-emerald-600 hover:bg-emerald-700 text-white text-sm rounded-sm flex items-center gap-2">
              <PrinterIcon size={12} weight="bold" />Print PDF
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
